import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTheme } from 'styled-components';

// Components
import { Loader } from '../../../inputs/Loader';
import InputText from '../../../inputs/InputText';
import { InputTextArea } from '../../../inputs/InputTextArea';
import DropDownInput from '../../../inputs/DropDown/DropDownInput';
import { ArrowDirectionButton } from '../../../inputs/buttons/ArrowDirectionButton';

// Icons
import { AlertIcon } from '../../../../assets/icons/AlertIcon';

// Hooks
import { useGpsToPK } from '../../../../hooks/useGpsToPK';
import { useClosingRoadStretches } from '../../../../hooks/useClosingRoadStretches';
import {
  useRoadDirectionLabels,
  useRoadPkRange,
} from '../../../../hooks/useRoadDirectionLabels';
import { roadPkRangeAFormulario } from '../../../../utils/pkValidation';

// Data

// Schema
import { changeDirectionSchema } from './schema';

// Styles — composición centrada espejo de PauseSurveillanceRouteModal.
import {
  ContainerForm,
  ContainerText,
  Title,
  Text,
  Section,
  PkFieldWrap,
  TextAreaWrap,
  ButtonRow,
  ActionButton,
  ModalContainerStyled,
  ContainerLoader,
} from './styles';

// Types
import { ChangeDirectionFormData, ChangeDirectionModalProps } from './types';
import { type PartialDirection } from '../../../inputs/buttons/ArrowDirectionButton/types';
import { WorkOrderDirection } from '../../../../types/workOrder';
import { UNSPECIFIC } from '../../../../types/utils';
import { registerPk } from '../../../../utils/pkField';

const formatPkString = (km: number, meter: number): string =>
  `${km}+${meter.toString().padStart(3, '0')}`;

const parsePkString = (pk: string): { km: number; meter: number } | null => {
  const cleaned = pk.replace(/\s+/g, '');
  const match = cleaned.match(/^(\d+)\+(\d{1,4})$/);
  if (!match) return null;
  return { km: Number(match[1]), meter: Number(match[2]) };
};

/**
 * V4 P12 — Modal `Canvi de sentit`.
 *
 * Registra un cambio de sentit siguiendo el patrón canónico `finishOrder+createOrder`:
 * 1. Cierra el tramo actual con `pauseCause=basic`, `pauseCauseDetail="CANVI DE SENTIT"`.
 * 2. Crea un nuevo tramo con sentit invertido, startDate=now().
 *
 * Los campos vienen prefilled desde el consumidor (TimerOverlay) — PK via GPS→PK
 * si está disponible, sentit invertido del tramo anterior,
 * descripció i18n-zada "CANVI DE SENTIT" editable. Todo editable excepto horas
 * (siempre `now()` al guardar, FR-12.1).
 */
export const ChangeDirectionModal = ({
  onClickClose,
  data,
}: ChangeDirectionModalProps): JSX.Element => {
  const { t } = useTranslation(['modals', 'common', 'forms']);
  const theme = useTheme();
  const isLoading = data?.isLoading ?? false;
  // Cargando 2026-07-20: la mutation tarda 1,5-4s contra Atlas — sin esto el
  // modal quedaba con botones vivos (doble-submit posible) y sin feedback.
  const [isSubmitting, setIsSubmitting] = useState(false);

  const defaultPkString = useMemo(() => {
    if (!data?.defaultPk) return '';
    return formatPkString(data.defaultPk.km, data.defaultPk.meter);
  }, [data?.defaultPk]);


  // Etiquetas Lleida/Girona bajo las flechas ↓/↑ — mismo dato que el
  // formulario de creación de OT (`road.directions`), leído de caché por
  // roadId del recorrido.
  const directionLabels = useRoadDirectionLabels(data?.roadId);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getFieldState,
    watch,
  } = useForm<ChangeDirectionFormData>({
    resolver: yupResolver(changeDirectionSchema),
    defaultValues: {
      pk: defaultPkString,
      direction: data?.defaultDirection ?? '',
      description:
        data?.defaultDescription ?? t('modals:changeDirection.description'),
    },
  });

  // Por qué carreteras pasó el tramo que se CIERRA. Mismo cálculo y mismo hook
  // que el modal de Finalizar: hasta hoy este camino no lo hacía, y el tramo
  // que cerraba un cambio de sentido dentro del ramal quedaba como la troncal
  // entera (C-25, 2026-09-09: 550 m de C-25z sin registrar).
  const pkTyped = watch('pk');
  const closingStretches = useClosingRoadStretches({
    routeId: data?.routeId,
    segmentRoadId: data?.roadId,
    roadIds: data?.roadIds,
    segmentStartDate: data?.segmentStartDate,
    segmentPkInit: data?.segmentPkInit,
    pkEnd: parsePkString(pkTyped ?? ''),
  });

  // V4 D13: prefill GPS→PK al montar — pivote del cambio de sentit. El PK
  // GPS es el punto donde físicamente está el operario; pkInit y pkEnd
  // coinciden (pivote instantáneo) pero el Tablet puede editarlos.
  const roadPkRange = useRoadPkRange(data?.roadId);
  const resolveGpsPk = useGpsToPK();
  useEffect(() => {
    const roadId = data?.roadId;
    if (!roadId || !navigator.geolocation) return;
    let cancelled = false;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        if (cancelled) return;
        // Multi-carretera (2026-08-15): un cambio de sentido no cambia de
        // carretera, así que el PK se resuelve SOLO contra la del tramo.
        const pk = await resolveGpsPk({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          roadIds: [roadId],
        });
        if (cancelled || !pk) return;
        const formatted = formatPkString(pk.pkKm, pk.pkMeter);
        // No pisar lo que el operario ya haya tecleado mientras el GPS→PK
        // resolvía (2026-07-17) — guard por campo.
        if (!getFieldState('pk').isDirty) {
          setValue('pk', formatted, {
            shouldValidate: false,
            shouldDirty: false,
          });
        }
      },
      () => {
        /* fallback a defaults si GPS falla */
      },
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 2000 }
    );
    return () => {
      cancelled = true;
    };
  }, []);


  /**
   * Siembra los extremos de la carretera en el formulario para que el
   * `.test('pk-validation')` del schema tenga contra qué comparar.
   *
   * Va por `setValue` y no por `defaultValues` porque `useRoadPkRange` resuelve
   * ASÍNCRONO: si el recorrido se abre en frío, la carretera llega después de
   * montar el modal. Mismo gesto que hace `NewRoute` al elegir carretera.
   */
  useEffect(() => {
    const extremos = roadPkRangeAFormulario(roadPkRange);
    setValue('pkInitRoad', extremos.pkInitRoad);
    setValue('pkEndRoad', extremos.pkEndRoad);
  }, [roadPkRange, setValue]);

  const handleClose = useCallback(() => {
    reset();
    onClickClose();
  }, [reset, onClickClose]);

  const onSubmit = useCallback(
    async (formData: ChangeDirectionFormData) => {
      if (!data?.onConfirm || isSubmitting) return;
      // Un ÚNICO PK: el punto de giro. Va a la vez como pkEnd (cierra y pinta
      // el tramo previo hasta aquí) y como pkInit (arranca el nuevo tramo).
      const pk = parsePkString(formData.pk);
      if (!pk) return;
      setIsSubmitting(true);
      try {
        await data.onConfirm({
          pkInitKm: pk.km,
          pkInitMeter: pk.meter,
          pkEndKm: pk.km,
          pkEndMeter: pk.meter,
          newDirection: formData.direction,
          // Un solo destino (2026-08-10): el tramo que se CIERRA. Ver el JSDoc
          // de `pauseCauseDetail` en `types.ts`.
          pauseCauseDetail: formData.description,
          // `undefined` = no tocar lo guardado, igual que en Finalizar.
          roadStretches: closingStretches ?? undefined,
        });
        // Cerrar el modal tras confirmar (mismo fix 2026-07-03 que sus hermanos
        // StartTimer/PauseTimer — solo reset() dejaba el modal montado).
        handleClose();
      } finally {
        setIsSubmitting(false);
      }
    },
    [data, handleClose, isSubmitting, closingStretches]
  );

  // Mapping idéntico a StartTimerModal (NewWorkOrderForm/Description.tsx).
  const directionOptions: PartialDirection = {
    top: WorkOrderDirection.TOP,
    down: WorkOrderDirection.DOWN,
    both: WorkOrderDirection.BOTH,
    [UNSPECIFIC]: UNSPECIFIC,
  };

  return (
    <ModalContainerStyled
      // width 60 + composición centrada con icono, como sus hermanos del
      // ciclo de patrulla StartTimer/PauseTimer (feedback usuario
      // 2026-07-14). El título va DENTRO — no se pasa `title`.
      width={60}
      onClickClose={handleClose}
      fill={theme.colors.black}
    >
      {isSubmitting && (
        <ContainerLoader>
          <Loader />
        </ContainerLoader>
      )}
      <ContainerForm onSubmit={handleSubmit(onSubmit)} isLoading={isSubmitting}>
        <AlertIcon />

        <ContainerText>
          <Title variant="semiBold">{t('modals:changeDirection.title')}</Title>
        </ContainerText>

        {/* UN solo PK: el punto de cambio de sentido (pivote). Es a la vez el
            PK final del tramo que se cierra y el PK inicial del nuevo — por eso
            un único campo (feedback usuario 2026-07-24). */}
        <Section>
          <Text size="S">{t('modals:changeDirection.pk')}</Text>
          <PkFieldWrap>
            <InputText
              {...registerPk(register, setValue, 'pk')}
              typeInput="normal"
              placeholder={t('common:pkExample')}
              inputSize="S"
              borderError={!!errors.pk}
              error={errors.pk?.message ? t(errors.pk.message) : undefined}
            />
          </PkFieldWrap>
        </Section>

        {/* Sentido — flechas preseleccionadas al sentido invertido (antes era
            un InputText con el valor crudo 'down'/'top', feedback 2026-07-14) */}
        <Section>
          <Text size="S">{t('modals:changeDirection.direction')}</Text>
          <Controller
            name="direction"
            control={control}
            render={({ field: { onChange, value } }) => (
              <ArrowDirectionButton
                directions={['down', 'both', 'top', UNSPECIFIC]}
                hasUnspecific
                name="direction"
                defaultValue={value}
                borderError={!!errors.direction}
                error={
                  errors.direction?.message
                    ? t(errors.direction.message)
                    : undefined
                }
                directionLabels={directionLabels}
                onChangeDirection={(dir) => {
                  onChange(directionOptions[dir] ?? '');
                }}
              />
            )}
          />
        </Section>

        <Section>
          <Text size="S">{t('modals:changeDirection.descriptionLabel')}</Text>
          <TextAreaWrap>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <InputTextArea
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  disabled={false}
                />
              )}
            />
          </TextAreaWrap>
        </Section>

        <ButtonRow>
          <ActionButton
            type="button"
            text={t('modals:changeDirection.cancel')}
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading || isSubmitting}
          />
          <ActionButton
            type="submit"
            text={t('modals:changeDirection.save')}
            disabled={isLoading || isSubmitting}
          />
        </ButtonRow>
      </ContainerForm>
    </ModalContainerStyled>
  );
};

export default ChangeDirectionModal;
